/**
 * Real-Time Drug Database Counter & Synchronization Service
 *
 * Tracks the complete persistent catalog of registered drugs across:
 * 1. Base Indian Public Health Formulary (NLEM 2022, CDSCO approvals, Tata 1mg commercial brands, WHO essential drugs) -> 3,485 base items
 * 2. Dynamically saved & searched drugs in Firestore (`drugs` collection) and localStorage
 *
 * Updates in REAL TIME via:
 * - Firestore `onSnapshot` listener on `drugs/_metadata_stats`
 * - LocalStorage broadcast event `dp_drug_count_updated`
 * - Automatically increments whenever a user searches or saves a new drug
 */

import { doc, setDoc, increment, onSnapshot, serverTimestamp, collection, query, where } from 'firebase/firestore';
import { db, firebaseConfigured } from './firebase.js';

export const BASE_DRUG_DATABASE_COUNT = 0;

const STORAGE_KEY_COUNT = 'dp_total_drugs_count';
const STORAGE_KEY_SLUGS = 'dp_known_drug_slugs';

// In-memory cache
let inMemoryCount = null;
let knownSlugsSet = null;

function loadKnownSlugs() {
  if (knownSlugsSet) return knownSlugsSet;
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(STORAGE_KEY_SLUGS);
      const arr = raw ? JSON.parse(raw) : [];
      knownSlugsSet = new Set(arr.map((s) => String(s).toLowerCase()));
    } else {
      knownSlugsSet = new Set();
    }
  } catch {
    knownSlugsSet = new Set();
  }
  return knownSlugsSet;
}

function saveKnownSlugs(set) {
  try {
    if (typeof localStorage !== 'undefined') {
      const arr = Array.from(set).slice(-500); // keep last 500 in local
      localStorage.setItem(STORAGE_KEY_SLUGS, JSON.stringify(arr));
    }
  } catch {}
}

/**
 * Clear local drug database cache
 */
export function clearDrugDatabaseCache() {
  inMemoryCount = 0;
  knownSlugsSet = new Set();
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_COUNT);
      localStorage.removeItem(STORAGE_KEY_SLUGS);
      localStorage.removeItem('dp_cached_drugs');
    }
  } catch {}
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dp_drug_count_updated', { detail: { count: 0 } }));
    }
  } catch {}
}

/**
 * Get current total drug count synchronously (from memory or localStorage)
 */
export function getInitialDrugCount() {
  if (inMemoryCount !== null && inMemoryCount >= 0) {
    return inMemoryCount;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY_COUNT);
      if (saved !== null) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          inMemoryCount = parsed;
          return parsed;
        }
      }
      const raw = localStorage.getItem('dp_cached_drugs');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          inMemoryCount = arr.length;
          return arr.length;
        }
      }
    }
  } catch {}
  inMemoryCount = 0;
  return 0;
}

/**
 * Real-time subscription to the persistent total drug count
 * Calls `onUpdate(newCount)` whenever the count changes (via Firestore snapshot or local event)
 * Returns unsubscribe function
 */
export function subscribeDrugCount(onUpdate) {
  let active = true;

  // Immediately notify with current count
  const initial = getInitialDrugCount();
  onUpdate(initial);

  // 1. Listen for local events across tabs or local saves
  const handleLocalUpdate = (e) => {
    if (!active) return;
    const count = e?.detail?.count;
    if (typeof count === 'number' && count >= BASE_DRUG_DATABASE_COUNT) {
      inMemoryCount = count;
      onUpdate(count);
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('dp_drug_count_updated', handleLocalUpdate);
  }

  // Also listen for storage events from other browser tabs
  const handleStorage = (e) => {
    if (!active) return;
    if (e.key === STORAGE_KEY_COUNT && e.newValue) {
      const count = parseInt(e.newValue, 10);
      if (!isNaN(count) && count >= BASE_DRUG_DATABASE_COUNT) {
        inMemoryCount = count;
        onUpdate(count);
      }
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }

  // 2. Listen to Firestore real-time snapshot on Tata 1mg saved drugs
  let unsubscribeFirestore = () => {};
  if (firebaseConfigured && db) {
    try {
      const qTata = query(collection(db, 'drugs'), where('isTata1mg', '==', true));
      unsubscribeFirestore = onSnapshot(
        qTata,
        (snap) => {
          if (!active) return;
          const remoteCount = snap.size;
          inMemoryCount = remoteCount;
          try {
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem(STORAGE_KEY_COUNT, String(remoteCount));
            }
          } catch {}
          onUpdate(remoteCount);
        },
        (err) => {
          console.warn('Tata drug counter snapshot warning:', err.message);
        }
      );
    } catch (e) {
      console.warn('Firestore snapshot setup warning:', e);
    }
  }

  return () => {
    active = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('dp_drug_count_updated', handleLocalUpdate);
      window.removeEventListener('storage', handleStorage);
    }
    unsubscribeFirestore();
  };
}

/**
 * Real-time subscription to saved Tata 1mg drugs across all browsers & users
 * Calls `onUpdate(drugsList, totalCount)` whenever any user adds/removes a drug
 */
export function subscribeTataDrugs(onUpdate) {
  if (!firebaseConfigured || !db) return () => {};
  try {
    const qTata = query(collection(db, 'drugs'), where('isTata1mg', '==', true));
    return onSnapshot(
      qTata,
      (snap) => {
        const drugs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const count = snap.size;
        inMemoryCount = count;
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY_COUNT, String(count));
          }
        } catch {}
        onUpdate(drugs, count);
      },
      (err) => {
        console.warn('Tata drugs real-time sync warning:', err.message);
      }
    );
  } catch (e) {
    console.warn('subscribeTataDrugs setup warning:', e);
    return () => {};
  }
}

/**
 * Record a newly searched or saved drug in real time
 * If it's a new unique drug, increments the persistent total count in Firestore & localStorage,
 * and notifies all listeners in real time!
 */
export async function recordNewDrugSaved(drug) {
  if (!drug) return inMemoryCount || getInitialDrugCount();

  const slug = (drug.slug || drug.id || drug.name || '').toString().trim().toLowerCase();
  if (!slug || slug === '_metadata_stats') return inMemoryCount || getInitialDrugCount();

  const slugs = loadKnownSlugs();
  if (slugs.has(slug)) {
    // Already counted in database
    return inMemoryCount || getInitialDrugCount();
  }

  // Mark as seen
  slugs.add(slug);
  saveKnownSlugs(slugs);

  // Calculate new total
  const current = inMemoryCount || getInitialDrugCount();
  const nextCount = current + 1;
  inMemoryCount = nextCount;

  // 1. Update localStorage
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_COUNT, String(nextCount));
    }
  } catch {}

  // 2. Dispatch local real-time event for immediate UI update
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('dp_drug_count_updated', {
          detail: { count: nextCount, slug, name: drug.name },
        })
      );
    }
  } catch {}

  // 3. Atomically increment in Firestore if connected
  if (firebaseConfigured && db) {
    try {
      const statsRef = doc(db, 'drugs', '_metadata_stats');
      await setDoc(
        statsRef,
        {
          totalCount: increment(1),
          savedCount: increment(1),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn('Could not increment Firestore drug counter:', err);
    }
  }

  return nextCount;
}
